import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { DropDownInput } from "../../components/DropDownInput";
import useComponentVisible from "../../hooks/useComponentVisible";
import useScrollLock from "../../hooks/useScrollLock";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { Eye, EyeOff } from "lucide-react";

import {
  signupAction,
  changeSignupModalDisplay,
  changeLoginModalDisplayAciton,
} from "../../actions/authActions.js";
import { clearServerErrors } from "../../actions/errors.js";
import { validatePhoneNumber } from "../../validations.js";

const SignUp = ({
  signupAction,
  serverErrors,
  clearServerErrors,
  displaySignupModal,
  changeSignupModalDisplay,
  changeLoginModalDisplayAciton,
}) => {
  const [langSwitcherRef, isLangSwicherVisalbe, setIsLangSwicherVisable] =
    useComponentVisible(false);

  useScrollLock(displaySignupModal === "block");

  const { t, i18n } = useTranslation();
  const SignupSchema = yup.object().shape({
    first_name: yup.string().required(),
    last_name: yup.string().required(),
    email: yup.string().email().required(),
    phone_number: yup
      .number()
      .required()
      .typeError("phone_number_type_error")
      .test({
        name: "phone_number",
        test: validatePhoneNumber,
      }),
    address: yup.string().required(),
    worker_type: yup.string().required(),
    password: yup.string().required(),
    confirm_password: yup
      .string()
      .required()
      .oneOf([yup.ref("password"), null], "passwords_must_match"),
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm({ mode: "onSubmit", resolver: yupResolver(SignupSchema) });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  useEffect(() => {
    clearServerErrors();
  }, []);
  return (
    <div
      id="myModalR"
      className="modal-background"
      style={{ display: displaySignupModal }}
    >
      <div className="registration-container animate__slideInDown">
        <span
          className="close_registration"
          onClick={() => changeSignupModalDisplay("none")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <g>
              <g>
                <path
                  stroke="#000"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M18 18l-6-6m0 0L6 6m6 6l6-6m-6 6l-6 6"
                ></path>
              </g>
            </g>
          </svg>

          {/* {t("close modal")} */}
        </span>

        <form onSubmit={handleSubmit(signupAction)} autoComplete="" noValidate>
          <h4 className="signup-title">{t("sign_up")}</h4>
          <label htmlFor="name">
            <p>{t("first_name")}</p>
            <input
              type="name"
              name="name"
              placeholder={t("first_name")}
              {...register("first_name")}
            />
            {errors.first_name && (
              <a className="error-text">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {t(errors.first_name?.message.key)}
              </a>
            )}
            {serverErrors.first_name && (
              <p className="help is-danger">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {serverErrors.first_name}
              </p>
            )}
          </label>

          <label htmlFor="lastname">
            <p>{t("last_name")}</p>
            <input
              type="lastname"
              name="lastname"
              placeholder={t("last_name")}
              {...register("last_name")}
            />
            {errors.last_name && (
              <a className="error-text">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {t(errors.last_name?.message.key)}
              </a>
            )}
            {serverErrors.last_name && (
              <p className="help is-danger">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {serverErrors.last_name}
              </p>
            )}
          </label>

          <label htmlFor="email">
            <p>{t("email")}</p>
            <input
              type="email"
              name="email"
              placeholder={t("email")}
              {...register("email")}
            />
            {errors.email && (
              <a className="error-text">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {t(errors.email?.message.key)}
              </a>
            )}
            {serverErrors.email && (
              <a className="help is-danger error-text">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {t(serverErrors.email)}
              </a>
            )}
          </label>
          <label htmlFor="number">
            <p>{t("phone_number")}</p>
            <input
              type="tel"
              name="phone"
              placeholder={t("phone_number")}
              {...register("phone_number")}
            />
            {errors.phone_number && (
              <a className="error-text">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {t(errors.phone_number?.message)}
              </a>
            )}
            {serverErrors.phone_number && (
              <a className="help is-danger error-text">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {t(serverErrors.phone_number)}
              </a>
            )}
          </label>

          <label htmlFor="password">
            <p>{t("password")}</p>
            <input
              className="input-user-password"
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder={t("password")}
              {...register("password")}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>

            {errors.password && (
              <a className="error-text">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {t(errors.password?.message.key)}
              </a>
            )}
            {serverErrors.password && (
              <p>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {serverErrors.password}
              </p>
            )}
          </label>

          <label htmlFor="password">
            <p>{t("confirm_password")}</p>
            <input
              className="input-user-password"
              type={showConfirmPassword ? "text" : "password"}
              name="psw-repeat"
              placeholder={t("password")}
              {...register("confirm_password")}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowConfirmPassword((v) => !v)}
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
            {errors.confirm_password && (
              <a className="error-text">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {t(errors.confirm_password?.message)}
              </a>
            )}
            {serverErrors.confirm_password && (
              <p>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {serverErrors.confirm_password}
              </p>
            )}
          </label>

          <label>
            <p>{t("address")}</p>
            <input
              type="address"
              name="address"
              placeholder={t("address")}
              {...register("address")}
            />
            {errors.address && (
              <a className="error-text">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {t(errors.address?.message.key)}
              </a>
            )}
            {serverErrors.address && (
              <p>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {serverErrors.address}
              </p>
            )}
          </label>

          <label htmlFor="petwalker">
            <p>{t("get a job")}</p>
            <select {...register("worker_type")}>
              <option value="NONE">{t("noone")}</option>

              <option value="PET SITTER">{t("petsitter")}</option>

              <option value="PET WALKER">{t("petwalker")}</option>

              <option value="BOTH">{t("both")}</option>
            </select>
            {errors.worker_type && (
              <a className="error-text">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {t(errors.worker_type?.message)}
              </a>
            )}
            {serverErrors.worker_type && (
              <p>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                </svg>
                {serverErrors.worker_type}
              </p>
            )}
          </label>

          {serverErrors.error && (
            <p className="help is-danger">{t(serverErrors.error)}</p>
          )}
          <p className="registration-text">
            {t("registrer box text")}
            <Link
              to="/rules"
              onClick={() => {
                changeSignupModalDisplay("none");
              }}
            >
              {t("site rules")}
            </Link>
          </p>
          <input type="submit" value={t("sign_up")} />
        </form>
        <div className="login-signup-switcher">
          <p
            className="existing-account"
            onClick={() => {
              changeSignupModalDisplay("none");
              changeLoginModalDisplayAciton("block");
            }}
          >
            {t("with an existing account")} <span>{t("login")}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    serverErrors: state.errors.errors,
    displaySignupModal: state.userData.displaySignupModal,
  };
};

export default connect(mapStateToProps, {
  changeLoginModalDisplayAciton,
  signupAction,
  changeSignupModalDisplay,
  clearServerErrors,
})(SignUp);
